'use client';

import type { ReactNode } from 'react';
import { m, useReducedMotion } from 'motion/react';
import { usePathname } from 'next/navigation';

import { Breadcrumbs, type TBreadcrumbStep } from '@/components/breadcrumbs/breadcrumbs';
import { Heading } from '@/components/ui/heading/heading';
import { fadeSlideUp, staggerContainer } from '@/utils/animation/variants';

type PageHeaderProps = {
  caption?: string;
  title: string;
  highlight?: string;
  breadcrumbTitle?: string;
  /** An explicit trail — see `Breadcrumbs`. Wins over `breadcrumbTitle`. */
  breadcrumbItems?: TBreadcrumbStep[];
  /**
   * Pin the header to the vertical middle of the viewport on md+ (the default,
   * used by every content page). Listing pages that stack a filter panel under
   * the header pass `false`, so the header sits at the top of its column and
   * the panel below it can do the pinning (spec 013, R5).
   */
  sticky?: boolean;
  children?: ReactNode;
};

export const PageHeader = ({
  caption,
  title,
  highlight,
  breadcrumbTitle,
  breadcrumbItems,
  sticky = true,
  children,
}: PageHeaderProps) => {
  const prefersReducedMotion = useReducedMotion();
  const pathname = usePathname();

  return (
    <m.div
      key={pathname}
      initial={prefersReducedMotion ? false : 'hidden'}
      animate="visible"
      variants={staggerContainer}
      className={sticky ? 'md:sticky md:top-1/2 md:-translate-y-1/2' : undefined}
    >
      <m.div variants={fadeSlideUp} className="mb-8 overflow-hidden">
        <div className="min-w-0">
          <Breadcrumbs lastItemTitle={breadcrumbTitle} items={breadcrumbItems} />
        </div>
      </m.div>

      <m.div variants={fadeSlideUp}>
        {caption && (
          <div className="inline-block mb-2 text-xs font-oswald uppercase tracking-[0.35em] text-neutral-400">
            <span className="font-oswald">{caption}</span>
            <div className="flex items-center gap-2 mt-2 w-3/4">
              <div className="h-1 basis-1/3 rounded-full bg-action" />
              <div className="h-[2px] basis-2/3 rounded-full bg-neutral-700/70" />
            </div>
          </div>
        )}

        <Heading variant="h1" highlight={highlight}>
          {title}
        </Heading>
      </m.div>

      {children && <m.div variants={fadeSlideUp}>{children}</m.div>}
    </m.div>
  );
};
